import { SubAPI } from "./SubAPI";
import {Paginated} from "./Users";
import {HardwareGroupWithContent, HardwareItem, HwOpening} from "./Hardware";
import {EditHardwareGroupWithContent} from "../pages/project/shopdrawing/hardware/HwEditingContextProvider";
import {ShortCodeItem} from "./Library";
import {QuoteOpening} from "./QuoteOpenings";

export type ProductReplacement = {
    fromProduct: number;
    toProduct: number;
}

export type BulkEditItem = {
    id: number; // hardware group item id
    remove?: boolean;
    supplierCategory?: number;
    replaceWith?: {
        product: number;
        supplierCategory?: number;
    }[];
}

export class QuoteHardware extends SubAPI {
    bulkUpdateOpenings(input: {
        hardwareGroup: number;
        project: number;
        alternative: number;

        openings: QuoteOpening & {
            hwGroupAssign: string;
        }[];
    }) {
        return this.fetcher.post("/api/quote-hardware/bulk-update-openings", input);
    }

    reSequence(input: {
        project: number;
        alternative: number;
    }) {
        return this.fetcher.post("/api/quote-hardware/re-sequence", input);
    }

    forceRecalculate(input: {
        project: number;
        alternative: number;
    }) {
        return this.fetcher.post<{task: string}>("/api/quote-hardware/force-recalculate", input);
    }

    bulkEditProduct(input: {
        project: number;
        alternative: number;
        items: BulkEditItem[];
    }) {
        return this.fetcher.post("/api/quote-hardware/bulk-edit-product", input)
    }

    updateHardwareGroupLink(input: {
        opening: number;
        group: number;
        link: boolean
    }) {
        return this.fetcher.post("/api/quote-hardware/edit-opening-link", input)
    }

    upsertItemSecondary(input: HardwareItem) {
        return this.fetcher.post("/api/quote-hardware/item-secondary", input);
    }

    listWithContents(input: {
        project: number
        alternative: number;
    } | {
        groups: number[];
    }) {
        return this.fetcher.get<HardwareGroupWithContent[]>("/api/quote-hardware/with-content", input)
    }

    list(input: {
        project: number;
        alternative: number;
        search?: string;
        offset?: number;
    }) {
        return this.fetcher.get<Paginated<HardwareGroup>>("/api/quote-hardware", input)
    }

    upsert(input: HardwareGroupUpsert) {
        return this.fetcher.post<HardwareGroupUpsertResponse>("/api/quote-hardware", input)
    }

    upsertTask(input: HardwareGroupUpsert) {
        return this.fetcher.post<{task: string}>("/api/quote-hardware/task", input)
    }

    mergeCandidates(input: {
        project: number;
        alternative: number;
        splitFirst?: boolean;
    }) {
        return this.fetcher.get<MergeCandidate[]>("/api/quote-hardware/merge-candidates", input)
    }

    merge(input: {
        project: number;
        alternative: number;
        groups: MergeGroup[];
        resequence: boolean;
        autoMerge?: boolean;
    }) {
        return this.fetcher.post("/api/quote-hardware/merge", input)
    }
}

export type MergeGroup = {
    hwGroup: number;
    hwGroupName: string;
    fromHwGroup?: number;
    shortCodeName: string;
    shortCode: number;
    openings: number[];
}

export type MergeCandidate = {
    id: number;
    openings: (HwOpening & {
        doorName: string;
        hwGroupName: string;
        hwGroupId: number;
        shortCodeName: string;
        shortCodeId: number;
    })[];

    baseHwGroup: number;

    shortCodeItems: {
        product: number;
        productName: string;
        productCode: string;
        productFinishName: string;
        productFinishCode: string;
        productCategoryName?: string;
        qty: number;
    }[]
}

export type PrepDetail = {
    name: string;
    value: string | null; // if set to null, falls back to pre-calculated value
    enabled: boolean
}

// which prep entries an opening takes from its hardware group (legacy) instead of its drw elevations.
// See core/opening/topening/prepfromgroup.go.
export type PrepFromGroup = {
    overCutDoor: boolean;
    underCutDoor: boolean;
    underCutFrame: boolean;
    hardware: boolean;
}

export type HardwareGroupUpsertResponse = {
    updated: true;
    id: number;
    name: string;
} | {
    updated: false;
    confirmShortcodeChanges: ConfirmShortcodeChange

    // the hardware elevations the hinge change would update, when confirmElevationUpdates
    // wasn't set. Nothing was saved.
    elevationUpdates?: string[] | null;
}

export type PrepMigrationResolution = {
    prepMigrationResolution: PrepMigration
}

export type PrepMigration = {
    add: any;
    remove: any;
    change: any;

    addDescription: string[];
    removeDescription: string[];
    changeDescription: string[];
}

export type ConfirmShortcodeChange = {
    existingId?: number;
    existingName?: string;
    existingModifiesGroupNames: string[] | null;
    requiredChanges: ShortcodeItemChange[] | null;

    exactMatches: {id: number; name: string;}[] | null;
}

export type ShortcodeItemChange = Omit<ShortCodeItem, "id"> & {
    id?: number;
    newQty: number;
}

export type HardwareGroupUpsert = {
    value: EditHardwareGroupWithContent

    createShortcode?: {
        name: string;
        description: string;
    };

    updateShortcode?: number;
    linkToShortcode?: number;

    prepMigrationResolution?: PrepMigration[];

    // set once the user has agreed to update the hardware elevations listed in
    // HardwareGroupUpsertResponse.elevationUpdates.
    confirmElevationUpdates?: boolean;
}

export type HardwareGroup = {
    id: number;
    name: string;
    project: number;
    notes: string;
    prepConfigJson: PrepDetail[];
    quoteAlternative: number;
}